import api, { executeQuery } from '../utils/api';
import { PurchaseOrder, PurchaseOrderItem } from '../types';
import { getStoreId } from '../utils/storeUtilsClient';
import axios from 'axios';

// Get all purchase orders by store
export const getAllPurchaseOrders = async (storeIdParam?: number) => {
  const storeId = storeIdParam ?? await getStoreId();
  const res = await api.get(`/stores/${storeId}/purchase-orders`);
  return res.data;
};

// Get purchase order by ID with items
export const getPurchaseOrderById = async (
  id: number,
  storeIdOverride?: number,
  params?: {
    page?: number;
    pageSize?: number;
    keyword?: string;
  }
) => {
  const storeId = storeIdOverride ?? await getStoreId();
  const res = await api.get(`/stores/${storeId}/purchase-orders/${id}`, { params });
  return res.data;
};

// Create new purchase order
export const createPurchaseOrder = async (
  order: Omit<PurchaseOrder, 'id' | 'created_at' | 'updated_at'>,
  items: Omit<PurchaseOrderItem, 'id' | 'purchase_order_id'>[],
  storeIdParam?: number
) => {
  try {
    

    // 确保supplier_id是数字类型
    if (typeof order.supplier_id === 'string') {
      order.supplier_id = parseInt(order.supplier_id, 10);
    }

    // 确保状态值正确 - 始终使用"draft"状态创建订单
    // 后端会将其映射为"pending"
    const orderData = {
      ...order,
      status: 'draft' // 修改此处，确保使用数据库支持的状态值
    };

    // 处理商品项，确保product_id格式正确
    const formattedItems = items.map(item => {
      // 特殊处理：如果product_id是"0"，转换为数字0
      if (item.product_id === '0') {
        return {
          ...item,
          product_id: 0
        };
      }

      return item;
    });

    
    const storeId = storeIdParam ?? await getStoreId();
    const poNumber = (order as any).po_number || `PO-${Date.now()}`;
    const res = await api.post(`/stores/${storeId}/purchase-orders`, {
       po_number: poNumber,
        supplier_id: order.supplier_id,
        order_date: order.order_date,
        items: formattedItems,
        notes: (order as any).notes || ''
     });

    return {
      id: res.data.id,
      ...orderData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      items: formattedItems
    };
  } catch (error) {
    // Rollback transaction on error
    console.error('Error creating purchase order:', error);
    throw error;
  }
};

// Delete purchase order (draft only)
export const deletePurchaseOrder = async (id: number, storeIdParam?: number) => {
  const storeId = storeIdParam ?? await getStoreId();
  const response = await api.delete(`/stores/${storeId}/purchase-orders/${id}`);
  return response.data;
};

// Generate purchase orders from low stock items
export const generatePurchaseOrdersFromLowStock = async () => {
  try {
    
    const currentStoreId = await getStoreId();
    
    // Get low stock items - 只选择status=1(警告)的记录
    const lowStockQuery = `
      SELECT s.*, p.ArticuloID as product_id, p.ProveedorID as supplier_id, p.NombreES as product_name
      FROM stock s
      LEFT JOIN store_${currentStoreId}_articulo p ON s.ArticuloID = p.ArticuloID
      WHERE s.status = 1
    `;
    const lowStockRes = await api.get(`/stores/${currentStoreId}/stock`);
    const lowStockItems: any[] = lowStockRes.data || [];
    
    if (lowStockItems.length === 0) {
      
      return { message: 'No low stock items found' };
    }
    
    
    
    // 如果有找不到供应商ID的商品，尝试在其他商店中查找
    const itemsWithoutSupplier = lowStockItems.filter((item: any) => !item.supplier_id);
    if (itemsWithoutSupplier.length > 0) {
      
      
      // 获取所有店铺ID
      const storesRes = await api.get('/stores');
      const stores = storesRes.data || [];
      
      // 对于每个没有供应商信息的商品
      for (const item of itemsWithoutSupplier as any[]) {
        const articuloID = item.ArticuloID;
        
        // 遍历所有其他商店
        for (const store of stores) {
          const otherStoreId = parseInt(store.id);
          
          // 跳过当前已查询过的商店
          if (isNaN(otherStoreId) || otherStoreId === currentStoreId) {
            continue;
          }
          
          try {
            // 在其他商店查询产品信息
            const productQuery = `
              SELECT ArticuloID as product_id, ProveedorID as supplier_id, NombreES as product_name
              FROM store_${otherStoreId}_articulo
              WHERE ArticuloID = ?
            `;
            const productInfoRes = await api.get(`/stores/${otherStoreId}/stock/${articuloID}`);
            const productInfo = productInfoRes.data || [];
            
            // 如果找到产品信息，更新当前库存记录
            if (productInfo.length > 0 && productInfo[0].supplier_id) {
              Object.assign(item, productInfo[0]);
              
              break; // 找到供应商后停止查询其他商店
            }
          } catch (err) {
            // 忽略单个商店的查询错误，继续检查下一个
            console.error(`检查商店 ${otherStoreId} 时出错:`, err);
            continue;
          }
        }
      }
    }
    
    // 检查每个低库存商品是否已有正在进行中的采购订单
    const articuloIDs = lowStockItems.map((item: any) => item.ArticuloID);
    if (articuloIDs.length > 0) {
      // 查询所有未完成状态的采购订单物品（draft, ordered, partial状态）
      const openOrdersQuery = `
        SELECT poi.product_id, poi.purchase_order_id, po.status
        FROM purchase_order_items poi
        JOIN purchase_orders po ON poi.purchase_order_id = po.id
        WHERE po.status IN ('draft', 'ordered', 'partial')
        AND poi.product_id IN (?)
      `;
      
      try {
        const orderItemsRes = await api.get(`/stores/${currentStoreId}/purchase-orders/items`, { params: { product_ids: articuloIDs.join(',') } });
        const orderItems = orderItemsRes.data || [];
        
        
        // 创建映射表，存储每个商品ID对应的采购订单状态
        const orderStatusMap: {[key: string]: string} = {};
        orderItems.forEach((item: any) => {
          if (item.product_id) {
            orderStatusMap[item.product_id] = item.status;
          }
        });
        
        // 更新每个库存项的采购订单状态
        lowStockItems.forEach((item: any) => {
          item.orderStatus = orderStatusMap[item.product_id] || null;
        });
      } catch (err) {
        console.error('查询采购订单状态时出错:', err);
      }
    }

    // 注意：删除导致超时的products表相关查询
    

    // 添加类型定义
    interface LowStockItem {
      ArticuloID: string;
      Stock: number;
      MiniStock: number;
      MaxiStock: number;
      product_id?: string | number;
      supplier_id?: string | number;
      product_name?: string;
      [key: string]: any; // 其他可能的属性
    }

    // Group items by supplier
    interface OrderItem {
      product_id: string | number;
      quantity: number;
      unit_price: number;
    }
    
    const itemsBySupplier: Record<string | number, OrderItem[]> = {};
    
    for (const item of lowStockItems as LowStockItem[]) {
      if (!item.supplier_id) {
        
        continue;
      }
      
      const supplierId = item.supplier_id;
      
      if (!itemsBySupplier[supplierId]) {
        itemsBySupplier[supplierId] = [];
      }
      
      // Calculate quantity to order (MiniStock - current Stock)
      const quantityToOrder = item.MiniStock - item.Stock;
      
      itemsBySupplier[supplierId].push({
        product_id: item.product_id || item.ArticuloID,
        quantity: quantityToOrder > 0 ? quantityToOrder : 0,
        unit_price: item.unit_price || 0
      });
    }
    
    // Create purchase orders for each supplier
    const createdOrders = [];
    
    for (const supplierId in itemsBySupplier) {
      const items = itemsBySupplier[supplierId];
      
      if (items.length === 0) continue;
      
      try {
        
        const purchaseOrder = await createPurchaseOrder(
          {
            supplier_id: parseInt(supplierId),
            status: 'draft'
          },
          items
        );
        
        createdOrders.push(purchaseOrder);
        
      } catch (err) {
        console.error(`为供应商 ${supplierId} 创建采购订单失败:`, err);
        // 继续处理其他供应商
      }
    }
    
    return createdOrders.length > 0 
      ? createdOrders 
      : { message: 'No purchase orders were created', reason: 'Either no low stock items or missing supplier information' };
      
  } catch (error) {
    console.error('Error generating purchase orders from low stock:', error);
    throw error;
  }
};

// new simplified workflow helpers
export const submitPurchaseOrder = async (id:number, storeIdParam?: number)=>{
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/submit`);
};

export const approvePurchaseOrder = async (id:number, storeIdParam?: number)=>{
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/approve`);
};

export const rejectPurchaseOrder = async (id:number, storeIdParam?: number)=>{
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/reject`);
};

export const receivePurchaseOrder = async (
  id:number,
  received_items:any[],
  storeIdParam?: number,
  warehouse_id?: number,
  options?: { receive_all_remaining?: boolean; use_recorded_received_quantities?: boolean; preserve_touched_received_items?: boolean }
)=>{
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/receive`, {
    received_items,
    warehouse_id,
    ...(options?.receive_all_remaining ? { receive_all_remaining: true } : {}),
    ...(options?.use_recorded_received_quantities ? { use_recorded_received_quantities: true } : {}),
    ...(options?.preserve_touched_received_items ? { preserve_touched_received_items: true } : {}),
  });
};

// 已收货订单返回草稿（仅超级管理员），同时扣减已入库的库存
export const revertPurchaseOrderToDraft = async (id: number, storeIdParam?: number) => {
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/revert-to-draft`);
};

// 仓库人员登记到货数量（submitted -> goods_received），不触发库存写入
export const registerReceiptPurchaseOrder = async (
  id:number,
  received_items:any[],
  storeIdParam?: number,
  options?: { receive_all_remaining?: boolean; preserve_touched_received_items?: boolean }
)=>{
  const storeId = storeIdParam ?? await getStoreId();
  return api.put(`/stores/${storeId}/purchase-orders/${id}/register-receipt`, {
    received_items,
    ...(options?.receive_all_remaining ? { receive_all_remaining: true } : {}),
    ...(options?.preserve_touched_received_items ? { preserve_touched_received_items: true } : {}),
  });
};

// 更新采购订单
export const updatePurchaseOrder = async (id: number, updateData: any) => {
  try {
    const response = await api.put(`/purchase-orders/${id}`, updateData);
    return response.data;
  } catch (error) {
    console.error('更新采购订单失败:', error);
    throw error;
  }
};

// 更新草稿订单的单个明细项（数量/单价/备注）
export const updatePurchaseOrderItem = async (
  storeId: number,
  orderId: number,
  itemId: number,
  updateData: {
    quantity?: number;
    unit_price?: number;
    notes?: string;
    barcode?: string;
    product_code?: string;
    product_name?: string;
    wholesale_price?: number;
    multiplier?: number;
    retail_price?: number;
    member_price?: number;
  }
) => {
  const response = await api.put(`/stores/${storeId}/purchase-orders/${orderId}/items/${itemId}`, updateData);
  return response.data;
};

// 草稿订单新增明细项
export const addPurchaseOrderItem = async (
  storeId: number,
  orderId: number,
  itemData: {
    product_id?: string | number;
    barcode?: string;
    product_code?: string;
    product_name?: string;
    quantity?: number;
    unit_price?: number;
    wholesale_price?: number;
    multiplier?: number;
    retail_price?: number;
    member_price?: number;
  }
) => {
  const response = await api.post(`/stores/${storeId}/purchase-orders/${orderId}/items`, itemData);
  return response.data;
};

// 草稿订单删除明细项
export const deletePurchaseOrderItem = async (storeId: number, orderId: number, itemId: number) => {
  const response = await api.delete(`/stores/${storeId}/purchase-orders/${orderId}/items/${itemId}`);
  return response.data;
};

// 更新草稿订单的备注/供应商
export const patchDraftOrder = async (
  storeId: number,
  orderId: number,
  updateData: { notes?: string; supplier_id?: number | string }
) => {
  const response = await api.patch(`/stores/${storeId}/purchase-orders/${orderId}/draft`, updateData);
  return response.data;
};

// NOTE: old updatePurchaseOrderStatus is obsolete with new workflow

export const generateShareToken = async (poId:number)=>{
  try{
    const res = await api.post(`/share/purchase/${poId}/token`);
    return res.data;
  }catch(err){
    console.error('生成分享Token失败:',err);
    throw err;
  }
};

// 批量导入采购订单
export const importPurchaseOrder = async (
  storeId: number,
  items: any[],
  supplierId?: number | string,
  autoCreateProducts: boolean = false,
  warehouseId?: number | null,
  options?: {
    orderStatus?: 'draft' | 'submitted';
    syncProductFields?: boolean;
    notes?: string;
    checkedFields?: string[];
  }
) => {
  try {
    
    
    // 检查第一个商品的数据结构
    if (items.length > 0) {
      
    }
    
    const requestBody: any = {
      items,
      supplier_id: supplierId,
      auto_create_products: autoCreateProducts // 确保这个参数被正确传递
    };

    if (warehouseId !== undefined && warehouseId !== null) {
      requestBody.warehouse_id = warehouseId;
    }

    if (options?.orderStatus) {
      requestBody.order_status = options.orderStatus;
    }

    if (options?.syncProductFields !== undefined) {
      requestBody.sync_product_fields = options.syncProductFields;
    }

    if (options?.notes !== undefined) {
      requestBody.notes = options.notes;
    }

    if (options?.checkedFields !== undefined) {
      requestBody.checked_fields = options.checkedFields;
    }

    const response = await api.post(`/stores/${storeId}/purchase-orders/import`, requestBody);
    
    
    
    // 检查是否有创建的产品
    if (response.data.created_products) {
      
    } else if (autoCreateProducts) {
      
    }
    
    return response.data;
  } catch (error: any) {
    console.error('批量导入采购订单失败:', error);
    
    // 提取更有用的错误信息
    let errorMessage = '导入失败';
    if (error.response && error.response.data) {
      // 服务器返回的错误信息
      errorMessage = error.response.data.message || error.response.data.error || errorMessage;
    } else if (error.message) {
      // Axios或JavaScript错误
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
}; 

export const createPurchaseImportJob = async (
  storeId: number,
  items: any[],
  supplierId?: number | string,
  autoCreateProducts: boolean = false,
  warehouseId?: number | null,
  checkedFields?: string[]
) => {
  const requestBody: any = {
    items,
    supplier_id: supplierId,
    auto_create_products: autoCreateProducts
  };

  if (warehouseId !== undefined && warehouseId !== null) {
    requestBody.warehouse_id = warehouseId;
  }

  if (checkedFields !== undefined) {
    requestBody.checked_fields = checkedFields;
  }

  const response = await api.post(`/stores/${storeId}/purchase-orders/import-jobs`, requestBody);
  return response.data;
};

export const getPurchaseImportJob = async (storeId: number, jobId: number) => {
  const response = await api.get(`/stores/${storeId}/purchase-orders/import-jobs/${jobId}`);
  return response.data;
};

